import cloneDeep from 'lodash/cloneDeep';
import { type JobListItem, type JobPayload } from 'src/services/jobsService';
import { createTags, extractAvailableFields, filterByBooleans, filterByStrings } from 'src/utils/tableUtils';

import { type SiteType, type Type } from '../store/createNewJobSlice';
import {
  type FilterTag,
  type JobConfigTableDataRow,
  type JobConfigurationTableFilter,
} from '../store/jobConfigPageSlice/types';
import { type Site } from '../store/siteSlice';

import { type Region } from './tasks/types';

class JobConfigPageService {
  /**
   * JobItem to tableRow
   */
  public jobListItemToTableRow(job: JobListItem) {
    return {
      ...job,
      applicableSites: [],
      jobApplicability: '',
      tasks: [],
    };
  }

  /**
   * Serialize requirement value
   */
  public serializeRequirementValue(requirementValue: string) {
    if (requirementValue === 'Yes' || requirementValue === 'No') {
      return requirementValue === 'Yes' ? '1' : '0';
    }
    return requirementValue;
  }

  /**
   * TableRow to Job payload
   */
  public tableRowToJobPayload(
    row: JobConfigTableDataRow,
    regions: Region[],
    jobTypes: Type[],
    siteTypes: SiteType[],
    specificSites: Site[],
  ) {
    return {
      id: row.id,
      name: row.name,
      description: row.description,
      siteSpecific: row.jobApplicability === 'siteSpecific',
      active: row.active || false,
      region: {
        id: regions.filter((r) => r.name === row.region)[0].id,
        name: row.region,
      },
      jobType: {
        id: jobTypes.filter((t) => t.name === row.jobType)[0].id,
        name: row.jobType,
      },
      siteType:
        row.jobApplicability === 'siteType'
          ? {
              id: siteTypes.filter((s) => s.label === row.siteType)[0].id,
              label: row.siteType,
            }
          : null,
      specificSites:
        row.jobApplicability === 'siteSpecific'
          ? row.applicableSites?.map((siteName) => ({
              id: specificSites.filter((s) => s.site === siteName)[0].id,
              site: siteName,
            }))
          : [],
      jobRequirements: row.tasks?.map((task) => ({
        task: {
          id: task.taskId,
          label: task.label,
        },
        bodyLocation: task.identifyBodyLocation ? task.bodyLocationType : null,
        value: this.serializeRequirementValue(task.requirementName as string),
      })),
      isForTierTwoPlusRoles: row.isForTierTwoPlusRoles,
      isTierTwoPlusJobModified: row.isTierTwoPlusJobModified,
    } as JobPayload;
  }

  /**
   * Setup the Job Configuration Filters from the table data.
   */
  public setupJobConfigFilterOptionsFromTableRows(tableData: JobConfigTableDataRow[]): JobConfigurationTableFilter {
    return {
      jobNames: extractAvailableFields(tableData, 'name'),
      regions: extractAvailableFields(tableData, 'region'),
      types: extractAvailableFields(tableData, 'jobType'),
      siteTypes: extractAvailableFields(tableData, 'siteType'),
      isActiveSelections: ['Yes', 'No'],
    };
  }

  /**
   * Setup filtered table data using the JobConfig Table filter.
   */
  public applyJobConfigTableFilterUtil(
    tableData: JobConfigTableDataRow[],
    filter: JobConfigurationTableFilter,
  ): JobConfigTableDataRow[] {
    let filteredData = cloneDeep(tableData);
    filteredData = filterByStrings(filteredData, 'name', filter.jobNames);
    filteredData = filterByStrings(filteredData, 'region', filter.regions);
    filteredData = filterByStrings(filteredData, 'jobType', filter.types);
    filteredData = filterByStrings(filteredData, 'siteType', filter.siteTypes);
    filteredData = filterByBooleans(
      filteredData,
      'active',
      filter.isActiveSelections.map((isActive) => isActive === 'Yes'),
    );
    return filteredData;
  }

  /**
   * Get the applied filter tabl from the JobConfig Table filter.
   */
  public getAppliedFilterTagsFromJobConfigTableFilter(filter: JobConfigurationTableFilter): FilterTag[] {
    return [
      ...createTags(filter, 'jobNames', 'Name'),
      ...createTags(filter, 'regions', 'Region'),
      ...createTags(filter, 'types', 'Type'),
      ...createTags(filter, 'siteTypes', 'Site Type'),
      ...createTags(filter, 'isActiveSelections', 'Is Active'),
    ];
  }
}

export default new JobConfigPageService();
